/*  Iter 31.321 — Handoff Inbox
    -------------------------------------------------------------
    Operator-facing audit surface for the Gemini → Two handoff
    queue introduced in iter 31.315. Lists everything the operator
    filed from `/chat/gemini`; supports filter (all / pending /
    resolved), inline detail expansion (transcript, metadata), and
    a Mark-Resolved action that hits `PATCH /api/handoffs/<id>`.

    Auth guard: reuses the `<Protected>` wrapper mounted on the
    route in `App.js`. No new session logic here.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, CheckCircle2, ChevronRight, ChevronDown,
    Inbox, Clock, Archive,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function api() {
    const token = localStorage.getItem("shed_jwt");
    return axios.create({
        baseURL: `${API}/api`,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
}

const STATUS_STYLE = {
    pending:  { label: "PENDING",  cls: "bg-stamp-red/20 text-stamp-red border-stamp-red/50" },
    done:     { label: "RESOLVED", cls: "bg-stamp-green/20 text-stamp-green border-stamp-green/50" },
    partial:  { label: "PARTIAL",  cls: "bg-stamp-gold/20 text-stamp-gold border-stamp-gold/50" },
    declined: { label: "DECLINED", cls: "bg-ink/20 text-ink-soft border-ink/50" },
};

function StatusPill({ status }) {
    const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
    return (
        <span
            data-testid={`handoff-status-${status}`}
            className={`inline-flex items-center px-2 py-0.5 border-2 uppercase tracking-[0.18em] font-heading text-[10px] ${s.cls}`}
        >
            {s.label}
        </span>
    );
}

function shortDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso.slice(0, 16); }
}

function ResolveForm({ handoff, onDone, onCancel }) {
    const [note, setNote] = useState("");
    const [outcome, setOutcome] = useState("done");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!note.trim()) {
            toast.error("Add a note before resolving.");
            return;
        }
        setBusy(true);
        try {
            await api().patch(`/handoffs/${handoff.id}`, {
                outcome, note: note.trim(),
            });
            toast.success("Handoff resolved.");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 p-3 border-2 border-stamp-blue/40 bg-stamp-blue/5" data-testid="handoff-resolve-form">
            <div className="font-heading uppercase tracking-[0.22em] text-[10px] text-stamp-blue mb-2">
                Mark resolved
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
                {["done", "partial", "declined"].map((o) => (
                    <button
                        key={o}
                        type="button"
                        data-testid={`handoff-outcome-${o}`}
                        onClick={() => setOutcome(o)}
                        className={`font-heading uppercase text-[10px] tracking-[0.18em] px-2.5 py-1 border-2 ${
                            outcome === o
                                ? "border-stamp-blue bg-stamp-blue/15 text-stamp-blue"
                                : "border-ink/25 text-ink-soft hover:border-ink/50"
                        }`}
                    >{o}</button>
                ))}
            </div>
            <textarea
                data-testid="handoff-resolve-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What did Two do? (required)"
                rows={3}
                className="w-full font-body text-sm px-2 py-1.5 bg-paper-light border border-ink/25 focus:outline-none focus:border-stamp-blue text-ink"
            />
            <div className="flex items-center gap-2 mt-2">
                <button
                    type="button"
                    data-testid="handoff-resolve-submit"
                    onClick={submit}
                    disabled={busy}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-blue text-stamp-blue hover:bg-stamp-blue/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Resolve
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-ink/25 text-ink-soft hover:border-ink/50"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function HandoffDetail({ handoffId, onResolved }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [resolveOpen, setResolveOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api().get(`/handoffs/${handoffId}`);
                if (!cancelled) setDetail(data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || e.message);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [handoffId]);

    if (loading) {
        return <div className="p-4 text-ink-soft text-sm inline-flex items-center gap-2" data-testid="handoff-detail-loading">
            <Loader2 className="w-3 h-3 animate-spin" /> loading transcript…
        </div>;
    }
    if (err) return <div className="p-4 text-stamp-red text-sm" data-testid="handoff-detail-err">{err}</div>;
    if (!detail) return null;

    return (
        <div className="pl-6 pr-2 py-3 border-l-2 border-stamp-blue/40 bg-paper-dark/40" data-testid="handoff-detail">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-ink-soft mb-3">
                <div>id:      <span className="text-ink" data-testid="handoff-detail-id">{detail.id}</span></div>
                <div>status:  <StatusPill status={detail.status} /></div>
                <div>model:   <span className="text-ink">{detail.model || "—"}</span></div>
                <div>turns:   <span className="text-ink">{detail.turn_count}</span></div>
                <div>filed:   <span className="text-ink">{shortDate(detail.created_at)}</span></div>
                {detail.resolved_at ? (
                    <div>resolved: <span className="text-ink">{shortDate(detail.resolved_at)}</span></div>
                ) : <div />}
            </div>
            {detail.system_message ? (
                <details className="mb-3">
                    <summary className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft cursor-pointer hover:text-ink">
                        System message
                    </summary>
                    <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-ink-soft bg-paper-light border border-ink/15 p-2">{detail.system_message}</pre>
                </details>
            ) : null}
            <div className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft mb-1">
                Transcript ({detail.transcript.length})
            </div>
            <ol className="space-y-2" data-testid="handoff-detail-transcript">
                {detail.transcript.map((m, i) => (
                    <li key={i} className="text-ink" data-testid={`handoff-turn-${i}`}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-soft">
                            {m.role} · {shortDate(m.ts)}
                        </div>
                        <div className="font-body text-sm whitespace-pre-wrap">{m.content}</div>
                    </li>
                ))}
            </ol>
            {detail.status === "pending" ? (
                resolveOpen
                    ? <ResolveForm handoff={detail} onDone={() => { setResolveOpen(false); onResolved(); }} onCancel={() => setResolveOpen(false)} />
                    : <button
                        type="button"
                        data-testid="handoff-open-resolve"
                        onClick={() => setResolveOpen(true)}
                        className="mt-3 font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-blue text-stamp-blue hover:bg-stamp-blue/10 inline-flex items-center gap-1.5"
                    >
                        <CheckCircle2 className="w-3 h-3" /> Mark Resolved
                    </button>
            ) : null}
        </div>
    );
}

function HandoffRow({ handoff, expanded, onToggle, onResolved }) {
    return (
        <>
            <tr
                data-testid={`handoff-row-${handoff.id}`}
                onClick={onToggle}
                className={`border-b border-ink/10 hover:bg-paper-dark/30 cursor-pointer ${expanded ? "bg-paper-dark/30" : ""}`}
            >
                <td className="px-2 py-2 w-6 text-ink-soft">
                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </td>
                <td className="px-2 py-2 text-ink text-sm font-body">{handoff.subject}</td>
                <td className="px-2 py-2"><StatusPill status={handoff.status} /></td>
                <td className="px-2 py-2 text-ink-soft text-[11px] font-mono">{handoff.from_agent}</td>
                <td className="px-2 py-2 text-ink-soft text-[11px] font-mono">{handoff.to_agent}</td>
                <td className="px-2 py-2 text-ink-soft text-[11px] font-mono">{handoff.turn_count}</td>
                <td className="px-2 py-2 text-ink-soft text-[11px] font-mono">{shortDate(handoff.created_at)}</td>
                <td className="px-2 py-2 text-ink-soft text-sm max-w-[24ch] truncate" title={handoff.body_preview}>
                    {handoff.body_preview}
                </td>
            </tr>
            {expanded ? (
                <tr className="border-b border-ink/10">
                    <td colSpan={8} className="p-0">
                        <HandoffDetail handoffId={handoff.id} onResolved={onResolved} />
                    </td>
                </tr>
            ) : null}
        </>
    );
}

const TABS = [
    { key: "pending",  label: "Pending",  icon: Clock },
    { key: "resolved", label: "Resolved", icon: Archive },
    { key: "all",      label: "All",      icon: Inbox },
];

export default function Handoffs() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ pending: 0, resolved: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [listRes, statsRes] = await Promise.all([
                api().get(`/handoffs?status=${tab}&limit=50`),
                api().get(`/handoffs/stats`),
            ]);
            setItems(listRes.data?.items || []);
            setTotal(listRes.data?.total || 0);
            setStats(statsRes.data || { pending: 0, resolved: 0, total: 0 });
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally { setLoading(false); }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const handleResolved = useCallback(() => {
        setExpanded(null);
        load();
    }, [load]);

    const counts = useMemo(() => ({
        pending:  stats.pending,
        resolved: stats.resolved,
        all:      stats.total,
    }), [stats]);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="handoffs-page">
            <div className="max-w-6xl mx-auto p-6">
                <header className="mb-6 flex items-end justify-between border-b-2 border-ink/20 pb-3">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-soft mb-1">
                            The Shed · Inbox
                        </div>
                        <h1 className="font-heading uppercase tracking-[0.08em] text-2xl text-ink" data-testid="handoffs-title">
                            Handoffs
                        </h1>
                    </div>
                    <button
                        type="button"
                        data-testid="handoffs-refresh"
                        onClick={load}
                        disabled={loading}
                        className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-ink/25 text-ink-soft hover:border-ink/50 inline-flex items-center gap-1.5"
                    >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Refresh
                    </button>
                </header>

                <nav className="mb-4 flex items-center gap-2" data-testid="handoffs-tabs">
                    {TABS.map(({ key, label, icon: Icon }) => {
                        const active = tab === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                data-testid={`handoffs-tab-${key}`}
                                onClick={() => { setTab(key); setExpanded(null); }}
                                className={`font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-2 border-2 inline-flex items-center gap-2 ${
                                    active
                                        ? "border-stamp-blue bg-stamp-blue/10 text-stamp-blue"
                                        : "border-ink/20 text-ink-soft hover:border-ink/40"
                                }`}
                            >
                                <Icon className="w-3 h-3" />
                                {label}
                                <span className="font-mono text-[10px]">{counts[key] ?? 0}</span>
                            </button>
                        );
                    })}
                </nav>

                {err ? (
                    <div className="mb-4 p-3 border-2 border-stamp-red/40 bg-stamp-red/5 text-stamp-red text-sm" data-testid="handoffs-err">
                        {err}
                    </div>
                ) : null}

                {loading && items.length === 0 ? (
                    <div className="text-ink-soft text-sm inline-flex items-center gap-2" data-testid="handoffs-loading">
                        <Loader2 className="w-3 h-3 animate-spin" /> loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-ink-soft text-sm py-12 text-center font-body" data-testid="handoffs-empty">
                        No handoffs in this tab. When you file one from `/chat/gemini`, it lands here.
                    </div>
                ) : (
                    <div className="border-2 border-ink/15 bg-paper-light overflow-hidden">
                        <table className="w-full text-left" data-testid="handoffs-table">
                            <thead className="bg-paper-dark/50 border-b-2 border-ink/15">
                                <tr className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">
                                    <th className="px-2 py-2" />
                                    <th className="px-2 py-2">Subject</th>
                                    <th className="px-2 py-2">Status</th>
                                    <th className="px-2 py-2">From</th>
                                    <th className="px-2 py-2">To</th>
                                    <th className="px-2 py-2">Turns</th>
                                    <th className="px-2 py-2">Filed</th>
                                    <th className="px-2 py-2">Preview</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((h) => (
                                    <HandoffRow
                                        key={h.id}
                                        handoff={h}
                                        expanded={expanded === h.id}
                                        onToggle={() => setExpanded(expanded === h.id ? null : h.id)}
                                        onResolved={handleResolved}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="mt-3 text-ink-faint text-[11px] font-mono" data-testid="handoffs-footer">
                    Showing {items.length} of {total} · pending={stats.pending} resolved={stats.resolved}
                </div>
            </div>
        </div>
    );
}
